import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { getDateByDateTimeValue, useInterval } from '@elfsight/lib-utils';
import {
  useDisplayInSchedule,
  useTimeToEndSchedule
} from '@elfsight/widget-conditional-display';
import { setHours, setMinutes, setSeconds } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import type { TimerDefaultSettingsProps } from '../types';
import { increaseTimeByUnit } from '../utils';
import { usePersistentTimer } from './use-persistent-timer';

interface UseTimerValue extends TimerDefaultSettingsProps {
  onTimerRestart?: () => void;
}

export function useTimerValue({
  editMode,
  widgetId,
  id,
  timerType,
  repeatTimerEnabled,
  remainingRestartUnit,
  remainingRestartValue,
  timerDuration,
  durationUnit,
  scheduleStart,
  scheduleEnd,
  scheduleIsAllDay,
  scheduleTimeZone,
  scheduleRepeatPeriod,
  scheduleRepeatFrequency,
  scheduleRepeatInterval,
  scheduleRepeatWeeklyOnDays,
  scheduleRepeatMonthlyOnDay,
  scheduleRepeatEnds,
  scheduleRepeatEndsDate,
  scheduleRepeatEndsOccurrences,
  onTimerRestart
}: UseTimerValue) {
  const { timeToTheEnd, recalculateTimeToTheEnd } = useTimeToEndSchedule({
    settings: {
      scheduleStart,
      scheduleEnd,
      scheduleIsAllDay,
      scheduleTimeZone,
      scheduleRepeatPeriod,
      scheduleRepeatFrequency,
      scheduleRepeatInterval,
      scheduleRepeatWeeklyOnDays,
      scheduleRepeatMonthlyOnDay,
      scheduleRepeatEnds,
      scheduleRepeatEndsDate,
      scheduleRepeatEndsOccurrences
    },
    enabled: timerType === 'scheduled'
  });

  const handleOnTimerRestart = useCallback(() => {
    recalculateTimeToTheEnd();
    onTimerRestart?.();
  }, [recalculateTimeToTheEnd, onTimerRestart]);

  const displayTimer = useDisplayInSchedule({
    settings: {
      scheduleStart,
      scheduleEnd,
      scheduleIsAllDay,
      scheduleTimeZone,
      scheduleRepeatPeriod,
      scheduleRepeatFrequency,
      scheduleRepeatInterval,
      scheduleRepeatWeeklyOnDays,
      scheduleRepeatMonthlyOnDay,
      scheduleRepeatEnds,
      scheduleRepeatEndsDate,
      scheduleRepeatEndsOccurrences
    },
    enabled: timerType === 'scheduled',
    onRestart: handleOnTimerRestart
  });

  const startTime = useMemo(() => {
    const startDate = getDateByDateTimeValue(scheduleStart, scheduleTimeZone);
    const derivedStartDate = scheduleIsAllDay
      ? setMinutes(setHours(startDate, 0), 0)
      : startDate;

    return derivedStartDate.getTime();
  }, [scheduleStart, scheduleTimeZone, scheduleIsAllDay]);

  const endTime = useMemo(() => {
    const endDate = getDateByDateTimeValue(scheduleEnd, scheduleTimeZone);
    const derivedEndDate = scheduleIsAllDay
      ? setSeconds(setMinutes(setHours(endDate, 24), 0), 0)
      : endDate;

    return derivedEndDate.getTime();
  }, [scheduleEnd, scheduleTimeZone, scheduleIsAllDay]);

  const getNowWithTimeZone = useCallback(() => {
    const currentDate = scheduleTimeZone
      ? utcToZonedTime(new Date(), scheduleTimeZone, {})
      : new Date();

    return currentDate.getTime();
  }, [scheduleTimeZone]);

  const recalculateTimerState = useCallback(() => {
    if (!timeToTheEnd) {
      return { value: 0, isPaused: false };
    }

    if (startTime > getNowWithTimeZone()) {
      return { value: endTime - startTime, isPaused: true };
    }

    return { value: displayTimer ? timeToTheEnd : 0, isPaused: false };
  }, [displayTimer, timeToTheEnd, startTime, endTime, getNowWithTimeZone]);

  const [timerState, setTimerState] = useState(recalculateTimerState);

  useLayoutEffect(() => {
    setTimerState(recalculateTimerState);
  }, [
    recalculateTimerState,
    scheduleRepeatPeriod,
    scheduleRepeatFrequency,
    scheduleRepeatInterval,
    scheduleRepeatWeeklyOnDays,
    scheduleRepeatMonthlyOnDay,
    scheduleRepeatEnds,
    scheduleRepeatEndsDate,
    scheduleRepeatEndsOccurrences
  ]);

  const { isPaused } = timerState;
  useInterval(
    () => {
      if (startTime > getNowWithTimeZone()) {
        setTimerState({ ...timerState, value: endTime - startTime });
      } else {
        setTimerState({ ...timerState, isPaused: false });
      }
    },
    !isPaused ? null : 1000
  );

  const increasedTime = useMemo(
    () =>
      increaseTimeByUnit({
        time: Date.now(),
        duration: timerDuration,
        unit: durationUnit
      }),
    [timerDuration, durationUnit]
  );

  const durationMs = useMemo(() => increasedTime - Date.now(), [increasedTime]);

  const userRemaining = usePersistentTimer({
    options: {
      durationMs,
      remainingRestartUnit,
      remainingRestartValue,
      repeatTimerEnabled
    },
    disabled: timerType !== 'user-specific',
    editMode,
    widgetId,
    id,
    onTimerRestart
  });

  if (timerType === 'user-specific') {
    return {
      value: userRemaining,
      isPaused: false
    };
  }

  return timerState;
}
